import * as _ from 'lodash';
import { ODataModel, ODataObject } from '../../interfaces/odata-model.interface';
import {
    JsonTreeViewModelOptions,
    TreeViewModelOptions,
    TreeViewModelOptionsFromJson,
} from '../options/treeview-model-options';
import { JsonTreeViewNode, TreeViewNode, TreeViewNodeFromJson } from './tree-view-node';

export class TreeViewModel<T extends ODataModel> implements ODataModel {
    public selectedEntries: Array<TreeViewNode<T>> = [];

    public get selectedEntry(): TreeViewNode<T> {
        return this.selectedEntries[0];
    }
    public constructor(
        public name: string,
        public value: TreeViewNode<T>, // rootNode
        public options = new TreeViewModelOptions(),
    ) {}

    public getOutModel(): ODataTreeViewOutModel {
        const outModel: ODataTreeViewOutModel = {};
        outModel[this.name] = '';

        if (this.options.maxSelections === 1 && this.selectedEntries.length === 1) {
            const selectedOutModel = this.selectedEntry.payload.getOutModel();
            outModel[this.name] = selectedOutModel[this.selectedEntry.payload.name];
        } else if (this.options.maxSelections > 1) {
            outModel[this.name] = this.selectedEntries.map((selectedEntry) => {
                const selectedOutModel = selectedEntry.payload.getOutModel();
                return selectedOutModel[selectedEntry.payload.name];
            });
        }

        return outModel;
    }

    public selectEntry(entry: TreeViewNode<T>): boolean {
        // select entry if entry was not selected before
        if (!entry.selected) {
            if (this.selectedEntries.length < this.options.maxSelections) {
                this.selectedEntries.push(entry);
                entry.selected = true;
            }
        } else {
            // remove selection if checkbox was unticked
            entry.selected = false;
            _.remove(this.selectedEntries, { id: entry.id });
        }

        // update parents 'selectedChildren' array
        let parent = entry.parent;
        while (parent) {
            // if entry was selected, all the parents need to have 'hasSelected = true'
            if (entry.selected) {
                parent.selectedChildren.push(entry.id);
            } else {
                _.remove(parent.selectedChildren, (id) => id === entry.id);
            }
            parent = parent.parent;
        }

        return entry.selected;
    }

    public selectEntryByIdAndTitle(id: string | number, title: string): boolean {
        // Find entry in tree
        let nodesToCheck: Array<TreeViewNode<T>> = [];
        nodesToCheck.push(this.value);
        while (nodesToCheck.length > 0) {
            const currentNode = nodesToCheck.shift();

            if (!currentNode) {
                continue;
            }

            if (currentNode.id.toString() === id.toString() && currentNode.value === title) {
                // we found what we were looking for. Select entry and return!
                return this.selectEntry(currentNode);
            }
            nodesToCheck = nodesToCheck.concat(currentNode.children);
        }

        return false;
    }
}

export class TreeViewModelFromJson<T extends ODataModel> extends TreeViewModel<T> {
    public constructor(type: new (...args: unknown[]) => T, data: JsonTreeViewModel) {
        super(data.name, new TreeViewNodeFromJson<T>(type, data.value), new TreeViewModelOptionsFromJson(data.options));

        // re-create parent relationships in children and selected entries
        const nodesToCheck: Array<TreeViewNode<T>> = [];
        nodesToCheck.push(this.value);
        while (nodesToCheck.length > 0) {
            const currentNode = nodesToCheck.shift();
            if (!currentNode) {
                continue;
            }

            currentNode.children.forEach((child) => {
                // re-create parent relationship
                child.parent = currentNode;

                // add child to list of nodes to be processed
                nodesToCheck.push(child);
            });

            // add node to selected entries if previously selected
            if (data.selectedEntries.findIndex((node) => node.id === currentNode.id) > -1) {
                this.selectEntry(currentNode);
            }
        }
    }
}

export interface JsonTreeViewModel {
    name: string;
    value: JsonTreeViewNode;
    options: JsonTreeViewModelOptions;
    selectedEntries: JsonTreeViewNode[];
}

export type ODataTreeViewOutModel = ODataObject;
